import React, { useState } from "react";
import { Link, NavLink } from "react-router-dom";
import {
  Leaf,
  Home,
  User,
  LogIn,
  FolderOpen,
  ShieldCheck,
  Plug,
  FileText,
  Menu,
  HelpCircle,
  ChevronDown,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useAuth } from "@/context/auth";
import { useUserRoles } from "@/hooks/useUserRoles";
import LogoutButton from "@/components/Auth/LogoutButton";

type NavItem = {
  to: string;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
};

const Header: React.FC = () => {
  const { user } = useAuth();
  const { isAdmin } = useUserRoles();
  const [open, setOpen] = useState(false);

  const items: NavItem[] = [{ to: "/", label: "Hem", icon: Home }];

  if (user) {
    items.push(
      { to: "/uppdrag", label: "Mina uppdrag", icon: FolderOpen },
      { to: "/dokument", label: "Dokument", icon: FileText },
      { to: "/hjalp", label: "Hjälp", icon: HelpCircle },
    );
  }

  const accountItems: NavItem[] = [];
  if (user) {
    accountItems.push(
      { to: "/profile", label: "Min profil", icon: User },
      { to: "/integrationer", label: "Integrationer", icon: Plug },
    );
    if (isAdmin) accountItems.push({ to: "/admin", label: "Admin", icon: ShieldCheck });
  }

  return (
    <header className="sticky top-0 z-50 w-full hero-dark-gradient text-on-hero border-b border-white/10 backdrop-blur-sm shadow-sm">
      <div className="flex h-14 md:h-16 items-center justify-between gap-2 px-4 md:px-6">
        <Link to="/" className="flex min-w-0 items-center gap-2 font-semibold text-terracotta-500">
          <Leaf className="h-5 w-5 shrink-0" />
          <span className="truncate text-sm sm:text-base">Byggarbetsmiljökontrollen</span>
        </Link>

        {/* Desktop navigation */}
        <nav className="hidden md:flex items-center gap-1">
          {items.map(({ to, label, icon: Icon }) => (
            <NavLink key={to} to={to} aria-label={label} end={to === "/"}>
              {({ isActive }) => (
                <Button
                  variant="ghost"
                  size="sm"
                  className={`gap-2 text-on-hero text-glow hover:bg-white/10 ${isActive ? "bg-white/15" : ""}`}
                >
                  <Icon className="h-4 w-4 icon-on-hero" />
                  <span>{label}</span>
                </Button>
              )}
            </NavLink>
          ))}

          {user ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  className="gap-2 ml-1 text-on-hero text-glow hover:bg-white/10"
                >
                  <User className="h-4 w-4 icon-on-hero" />
                  <span>{user.first_name || "Konto"}</span>
                  <ChevronDown className="h-3.5 w-3.5 icon-on-hero" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56 bg-popover">
                {accountItems.map(({ to, label, icon: Icon }) => (
                  <DropdownMenuItem key={to} asChild>
                    <Link to={to} className="flex items-center gap-2">
                      <Icon className="h-4 w-4" />
                      {label}
                    </Link>
                  </DropdownMenuItem>
                ))}
                <DropdownMenuSeparator />
                <div className="px-1 py-1">
                  <LogoutButton variant="ghost" className="w-full justify-start" />
                </div>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <Link to="/auth">
              <Button variant="hero-outline" size="sm" className="gap-2 ml-2">
                <LogIn className="h-4 w-4 icon-on-hero" />
                Logga in
              </Button>
            </Link>
          )}
        </nav>

        {/* Mobile navigation */}
        <div className="flex md:hidden items-center gap-2">
          {!user && (
            <Link to="/auth">
              <Button variant="hero-outline" size="sm" className="gap-2">
                <LogIn className="h-4 w-4 icon-on-hero" />
                Logga in
              </Button>
            </Link>
          )}
          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                aria-label="Öppna meny"
                className="text-on-hero hover:bg-white/10 min-h-11 min-w-11"
              >
                <Menu className="h-5 w-5 icon-on-hero" />
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-[86vw] max-w-xs p-0">
              <SheetHeader className="px-4 py-4 text-left">
                <SheetTitle className="text-base">Meny</SheetTitle>
              </SheetHeader>
              <nav className="flex flex-col px-2 pb-4">
                {items.map(({ to, label, icon: Icon }) => (
                  <NavLink
                    key={to}
                    to={to}
                    end={to === "/"}
                    onClick={() => setOpen(false)}
                    className={({ isActive }) =>
                      `flex items-center gap-3 rounded-md px-3 py-3 text-sm font-medium transition-colors ${
                        isActive ? "bg-accent text-accent-foreground" : "hover:bg-accent/60"
                      }`
                    }
                  >
                    <Icon className="h-4 w-4 shrink-0" />
                    <span className="truncate">{label}</span>
                  </NavLink>
                ))}

                {accountItems.length > 0 && (
                  <>
                    <div className="mt-4 px-3 pb-1 text-xs font-medium uppercase tracking-wide text-muted-foreground">
                      Konto
                    </div>
                    {accountItems.map(({ to, label, icon: Icon }) => (
                      <NavLink
                        key={to}
                        to={to}
                        onClick={() => setOpen(false)}
                        className={({ isActive }) =>
                          `flex items-center gap-3 rounded-md px-3 py-3 text-sm font-medium transition-colors ${
                            isActive ? "bg-accent text-accent-foreground" : "hover:bg-accent/60"
                          }`
                        }
                      >
                        <Icon className="h-4 w-4 shrink-0" />
                        <span className="truncate">{label}</span>
                      </NavLink>
                    ))}
                  </>
                )}

                <div className="px-3 pt-4">
                  {user ? (
                    <LogoutButton variant="outline" className="w-full" />
                  ) : (
                    <Link to="/auth" onClick={() => setOpen(false)}>
                      <Button variant="outline" className="w-full gap-2">
                        <LogIn className="h-4 w-4" />
                        Logga in
                      </Button>
                    </Link>
                  )}
                </div>
              </nav>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
};

export default Header;
